// packages/internal-sdk/src/public/public-agents/useAgentVersions.ts
import useSWR from 'swr';
import { agentVersionsService, isApiError } from '@dopomogai/api-client';
import { AgentKitError } from '../../types';
import type { PaginatedAgentVersions, ListVersionsParams } from '@dopomogai/types';

// The fetcher function needs to stringify the params to create a stable SWR key.
const versionsFetcher = async ([_key, params]: [string, ListVersionsParams]) => {
  const response = await agentVersionsService.listAgentVersions(params);
  if (isApiError(response)) {
    throw new AgentKitError('Failed to fetch agent versions', response);
  }
  return response;
};

/**
 * A hook to fetch a paginated list of AgentVersions, with optional filters.
 *
 * @param params - An object with filters like agent_id, profile_id, limit, offset, etc.
 */
export const useAgentVersions = (params: ListVersionsParams = {}) => {
  // SWR key is an array containing the endpoint name and the params object.
  // SWR will perform a deep comparison on the params object to avoid unnecessary re-fetches.
  const swrKey = params ? ['/versions', params] : null;

  const { data, error, isLoading, mutate } = useSWR<PaginatedAgentVersions, AgentKitError>(
    swrKey,
    versionsFetcher,
    {
      keepPreviousData: true, // Good for paginated UI to avoid flicker
      revalidateOnFocus: false,
    }
  );

  return {
    /** The paginated response object. */
    paginatedResponse: data,
    /** A flat array of the version items. */
    versions: data?.items ?? [],
    /** The total count of items matching the filter. */
    totalCount: data?.count ?? 0,
    isLoading,
    error,
    /** SWR's mutate function. */
    mutate,
  };
};
